/**
 * downloadOrShareBlob — reliable cross-platform "save this file" for the
 * Allure app.
 *
 * Environment matrix and what works:
 *   • Desktop browser         → anchor `download` attribute (native save)
 *   • iOS Safari + Chrome     → `navigator.share({ files })` (native share sheet)
 *   • Android Chrome browser  → `navigator.share({ files })` (native share sheet)
 *   • Capacitor Android app   → WebView is Chrome-based, so
 *                               `navigator.share({ files })` works too;
 *                               anchor `download` on a `blob:` URL is
 *                               silently ignored (no DownloadListener
 *                               registered on the WebView).
 *   • Capacitor iOS app       → same as iOS Safari.
 *   • PWA installed to home   → `navigator.share({ files })` still works
 *
 * Strategy: try `navigator.share({ files })` first — it produces a
 * native share sheet with "Save to Files", "Email", "Drive", "Print", etc.
 * on every mobile platform, gracefully covering the Capacitor download
 * gap without requiring a native plugin rebuild. Fall back to anchor
 * download (desktop-friendly) and finally to opening the blob URL in a
 * new tab so the user can still get to the file.
 */

const CAPACITOR_UA = 'AllureApp';

function isLikelyCapacitor() {
  try {
    return typeof navigator !== 'undefined'
      && !!navigator.userAgent
      && navigator.userAgent.includes(CAPACITOR_UA);
  } catch {
    return false;
  }
}

/**
 * Download or share a Blob so the user can save/open it on their device.
 * Returns { method: 'share' | 'anchor' | 'window-open' | 'failed', cancelled?: boolean }.
 */
export async function downloadOrShareBlob(blob, filename, { mimeType } = {}) {
  if (!blob) return { method: 'failed' };
  const type = mimeType || blob.type || 'application/octet-stream';
  const safeName = (filename || 'download')
    .replace(/[/\\?%*:|"<>]/g, '-')
    .slice(0, 120);

  // 1) Web Share API with files — best on ALL mobile platforms including
  // Capacitor's Chrome WebView. Must be called synchronously from a user
  // gesture; the caller already is.
  try {
    if (typeof File !== 'undefined' && typeof navigator !== 'undefined' && navigator.canShare && navigator.share) {
      const file = new File([blob], safeName, { type });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: safeName });
          return { method: 'share' };
        } catch (err) {
          if (err && err.name === 'AbortError') {
            return { method: 'share', cancelled: true };
          }
          // fall through to anchor / window-open fallbacks
        }
      }
    }
  } catch {
    /* fall through */
  }

  // 2) Anchor download attribute — reliable on desktop browsers and iOS
  // Safari for direct saves. Skipped on Capacitor because its Android
  // WebView doesn't register a DownloadListener and silently drops the
  // click (this is the reported bug root cause).
  const blobUrl = URL.createObjectURL(blob);
  const capacitor = isLikelyCapacitor();
  try {
    if (!capacitor) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = safeName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoke after a delay so the browser has time to snapshot the URL.
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
      }, 30000);
      return { method: 'anchor' };
    }
  } catch {
    /* fall through */
  }

  // 3) Last-resort: open the blob URL in a new window / current WebView.
  try {
    const opened = window.open(blobUrl, '_blank', 'noopener');
    if (!opened) {
      // Blocked or unsupported (Capacitor WebView often ignores window.open
      // for blob: URLs). Navigate the current WebView to the URL so the
      // system PDF viewer can at least render inline — the user can then
      // use the WebView's own menu to save.
      window.location.assign(blobUrl);
    }
    setTimeout(() => {
      try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
    }, 30000);
    return { method: 'window-open' };
  } catch {
    try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
    return { method: 'failed' };
  }
}

export default downloadOrShareBlob;
